/**
 * @file     ChatMatchCard.tsx
 * @purpose  Compact horizontal match card for the chat thread: thumbnail,
 *           price, address, key facts, BCRES attribution. Projects through
 *           cardAdapters.toDisplay so display rules (address withheld, media
 *           proxy, attribution) stay in lockstep with PropertyCard.
 */
'use client';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { mlsToCardProps, toDisplay } from '@/features/properties/services/cardAdapters';
import { mlsCardImageLoader, applyPhotoFallback } from '@/services/mls/mlsImageLoader';
import type { MlsListingCardData } from '@/types/listing';
import { buildMapPinHref } from '@/features/ai-chat/mapPinHref';

export function ChatMatchCard({ listing, onOpen, onOpenListing }: { listing: MlsListingCardData; onOpen?: () => void; onOpenListing?: (listing: MlsListingCardData) => void }) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const display = toDisplay(mlsToCardProps(listing));
  const mapHref = buildMapPinHref(listing, display);
  const facts = [
    display.beds != null ? `${display.beds} ${t('properties.card.bd', { defaultValue: 'bd' })}` : null,
    display.baths != null ? `${display.baths} ${t('properties.card.ba', { defaultValue: 'ba' })}` : null,
    display.sqft != null ? `${Math.round(display.sqft).toLocaleString('en-CA')} ${t('properties.card.sqft', { defaultValue: 'sqft' })}` : null,
    display.yearBuilt != null ? `${t('properties.card.built', { defaultValue: 'Built' })} ${display.yearBuilt}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Link
      href={display.href}
      style={{ textDecoration: 'none' }}
      onClick={(e) => {
        onOpen?.();
        // In AI Search the host handles the tap (focus map + open detail);
        // keep the href for right-click / crawl and let modifier/middle-click
        // open a new tab. Only intercept the plain left click.
        if (onOpenListing && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
          e.preventDefault();
          onOpenListing(listing);
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          borderRadius: 1,
          overflow: 'hidden',
          border: `1px solid ${theme.semanticColors.borderSubtle}`,
          backgroundColor: theme.semanticColors.surfaceCard,
          transition: 'border-color 0.15s',
          '&:hover': { borderColor: theme.semanticColors.borderStrong },
        }}
      >
        <Box sx={{ position: 'relative', width: 132, minHeight: 108, flexShrink: 0 }}>
          {display.primaryImageSrc ? (
            <Image
              src={display.primaryImageSrc}
              alt={display.title}
              fill
              sizes="132px"
              loader={mlsCardImageLoader}
              onError={(e) => applyPhotoFallback(e.currentTarget as HTMLImageElement)}
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <Box sx={{ position: 'absolute', inset: 0, backgroundColor: theme.semanticColors.backgroundSecondary }} />
          )}
        </Box>
        <Box sx={{ py: 1, pr: 1.5, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.semanticColors.textPrimary }}>
            {display.price != null ? `$${display.price.toLocaleString('en-CA')}` : ''}
          </Typography>
          <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 500, color: theme.semanticColors.textPrimary }}>
            {display.title}
          </Typography>
          <Typography noWrap sx={{ fontSize: 12.5, color: theme.semanticColors.textSecondary }}>
            {[display.subtitle, facts].filter(Boolean).join(' · ')}
          </Typography>
          {display.attribution && (
            <Typography noWrap sx={{ fontSize: 11, fontStyle: 'italic', color: theme.semanticColors.textMuted }}>
              {display.attribution}
            </Typography>
          )}
          <Box sx={{ mt: 'auto', pt: 0.5 }}>
            <Button
              component="span"
              size="small"
              variant="text"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(mapHref, '_blank', 'noopener,noreferrer');
              }}
              sx={{ borderRadius: 1, textTransform: 'none', fontSize: 12, px: 1, py: 0.25, minWidth: 0 }}
            >
              {t('aiChat.viewOnMap', 'View on map')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Link>
  );
}
